import PriorityQueue from "js-priority-queue";
import { distance, neighbours } from "lib/util/mesh";
import Debug from "debug";
const debug = Debug("roads");

// The road.
//
// This is the game's spine: `render.route` is the array of mesh vertices the player walks, indexed
// by `party.step`, and `render.features` is what is underfoot at each one. Everything about pacing
// — how long the journey is, how many towns are on it, what terrain the log describes — is decided
// here.
//
// It used to be one A* run over the whole map whose cost function added `distance * Math.exp(100)`
// to steer toward the nearest unvisited town. That term is about 2.7e43; the water penalty beside
// it was +100 and the slope penalty was slope/1000, and at double precision `1e43 + 100 === 1e43`,
// so both were annihilated. The road took no account of terrain at all: measured over six seeded
// worlds, the median route ran 14% through water and one ran 40%, and the median height along it
// was 0.002 on land that tops out near 0.35 — half the journey was at or below sea level. The same
// run also mutated its visited-set mid-search, so g-scores were compared across different
// visitation states and the search was not a valid A* — hence the 100,000-iteration bailout it
// shipped with.
//
// It is now waypoint selection followed by a sequence of ordinary A* legs. Two consequences worth
// knowing:
//
//   - Every waypoint is on the route *by construction*. A town only counts as being on the road if
//     `route.indexOf(cityVertex)` finds it (see getPolitical, and Political's roadCities filter),
//     and under the old router 4 of 15 towns made it — once only 3, one unlucky map away from
//     `roadCities[1]` being undefined in the story machine.
//   - Every edge cost is `distance * multiplier` with the multiplier >= 1, so straight-line
//     distance is an admissible, consistent heuristic and each leg is a real A*. Nothing here
//     needs an iteration cap.

// What calling at a town may add to the journey, in map units on a world one unit across, where
// the whole journey is about 0.9 end to end. This is the "veer off slightly" budget, and it is
// spent per town: a town almost on the line costs nearly nothing and is always taken, while one
// off to the side costs the trip out *and the trip back* and is refused.
//
// Measuring the detour rather than the distance to the road is the whole point. A town 0.15 to the
// north is 0.15 away by either measure, but reaching it adds 0.30, and the road that resulted ran
// out to it and straight back down again — a spur, not a road.
const DETOUR_BUDGET = 0.08;

// ...except that the road should not cross a world and meet nobody. If the budget buys fewer towns
// than this, the cheapest few are taken anyway — the story machine indexes `roadCities[1]`, and a
// journey with one stop on it is not a journey.
const MIN_TOWNS = 5;

// Terrain multipliers on a step's distance. None of them is a wall: this is a man on foot who will
// wade if wading is shorter, and the point is that he would rather not.
const COST = {
  // Water is passable and miserable. High enough that the road only crosses where crossing is much
  // shorter than going around, which is what a ford is.
  WATER: 10,
  // Scaled against the 90th percentile of land slope, so this is "how much worse is the steepest
  // tenth of the country than the flat" rather than a magnitude that depends on the height scale.
  SLOPE: 1,
  // A river is a crossing, not a barrier.
  RIVER: 1.5,
};

/**
 * The towns that share a landmass with the bulk of the others.
 *
 * Cities are placed on any land at all, islands included, and the road has to reach every waypoint
 * it is given. Raising the cost of water does nothing about a town across a strait — there is no
 * dry route to price against — so worlds kept turning up whose road forded a fifth of its length.
 * The fix is not to price the crossing but to not go: a flood fill over land gives the connected
 * components, and the road is confined to the largest one.
 */
function mainlandCities(mesh, h, cities) {
  const component = new Map();
  let current = 0;
  const sizes = [];

  for (let seed = 0; seed < h.length; seed++) {
    if (h[seed] <= 0 || component.has(seed)) continue;

    let size = 0;
    const stack = [seed];
    component.set(seed, current);

    while (stack.length > 0) {
      const node = stack.pop();
      size++;
      const nbs = neighbours(mesh, node);
      for (let i = 0; i < nbs.length; i++) {
        const next = nbs[i];
        if (h[next] <= 0 || component.has(next)) continue;
        component.set(next, current);
        stack.push(next);
      }
    }

    sizes.push(size);
    current++;
  }

  let mainland = 0;
  for (let i = 1; i < sizes.length; i++) {
    if (sizes[i] > sizes[mainland]) mainland = i;
  }

  const onMainland = cities.filter((city) => component.get(city) === mainland);
  // Never hand back fewer than two: a world where the towns are genuinely scattered still needs a
  // road, and a wet one is better than none.
  return onMainland.length >= 2 ? onMainland : cities;
}

/**
 * The towns the road will call at, in the order it reaches them.
 *
 * The road is the fast way west before it is anything else. So it starts as exactly that — the two
 * ends and nothing between — and towns are admitted one at a time, cheapest first, each inserted at
 * whichever point in the run costs least to bend around. When the cheapest remaining town costs
 * more than DETOUR_BUDGET, the road stops collecting and goes west.
 *
 * Cheapest insertion gets the ordering for free: a town enters between the two waypoints it
 * actually lies between, so the road cannot double back to reach it.
 *
 * The insertion test is straight-line, not terrain-weighted. Pricing it properly would mean an A*
 * run per town per gap per round, and the ranking barely moves — the legs themselves are still
 * routed on terrain, which is where it matters.
 *
 * Three selections were tried before this and all three are worth not repeating. Visiting all
 * fifteen towns by longitude runs about 1300 steps, five times the intended journey, because their
 * latitudes sawtooth. Greedy nearest-westward has no lookahead: it takes a cheap step north-west,
 * finds nothing beyond it, and pays the whole height of the map to get back south — it drew a road
 * up one side of the map and back down the other. Taking the towns nearest the fast route looks
 * right and is not: distance from the road is not what a detour costs, and the road grew spurs that
 * ran out to a town and straight back.
 */
function selectWaypoints(mesh, cities) {
  if (cities.length < 3) return cities.slice();

  const waypoints = [cities[0], cities[cities.length - 1]];
  const remaining = new Set(cities.slice(1, -1));

  while (remaining.size > 0) {
    let best = null;

    for (const city of remaining) {
      for (let i = 0; i < waypoints.length - 1; i++) {
        const from = waypoints[i];
        const to = waypoints[i + 1];
        const added =
          distance(mesh, from, city) +
          distance(mesh, city, to) -
          distance(mesh, from, to);

        if (best === null || added < best.added) {
          best = { city, at: i + 1, added };
        }
      }
    }

    const withinBudget = best.added <= DETOUR_BUDGET;
    const owedTowns = waypoints.length - 2 < MIN_TOWNS;
    if (!withinBudget && !owedTowns) break;

    waypoints.splice(best.at, 0, best.city);
    remaining.delete(best.city);
  }

  return waypoints;
}

/**
 * One leg, between two towns. A plain A* — no shared mutable state with the other legs, so its
 * g-scores mean one thing throughout.
 *
 * Returns the vertices from `start` to `goal` inclusive, or null if the mesh is disconnected
 * between them (which a Voronoi mesh should never be).
 */
function findLeg(mesh, start, goal, edgeCost) {
  const open = new PriorityQueue({ comparator: (a, b) => a.f - b.f });
  const gScore = new Map([[start, 0]]);
  const cameFrom = new Map();
  const closed = new Set();

  open.queue({ index: start, f: distance(mesh, start, goal) });

  while (open.length > 0) {
    const current = open.dequeue().index;

    if (current === goal) {
      const leg = [current];
      let node = current;
      while (cameFrom.has(node)) {
        node = cameFrom.get(node);
        leg.unshift(node);
      }
      return leg;
    }

    if (closed.has(current)) continue;
    closed.add(current);

    const nbs = neighbours(mesh, current);
    for (let i = 0; i < nbs.length; i++) {
      const next = nbs[i];
      if (closed.has(next)) continue;

      const tentative = gScore.get(current) + edgeCost(current, next);
      if (!gScore.has(next) || tentative < gScore.get(next)) {
        gScore.set(next, tentative);
        cameFrom.set(next, current);
        open.queue({ index: next, f: tentative + distance(mesh, next, goal) });
      }
    }
  }

  return null;
}

/**
 * What is underfoot at a vertex.
 *
 * The thresholds are quantiles of this world's own height and slope distributions, computed in
 * lib/map.js. They used to be the constants 30 and 150 compared against a height field that
 * `peaky` normalises to [0,1] and `setSeaLevel` then shifts — measured land tops out around 0.35,
 * so both tests were unconditionally true and the height half of the classifier was dead code.
 * Quantiles also mean the classifier survives any future change to the height pipeline's scale.
 */
function terrainAt(vertex, h, slopeData, riverSet, nearRiverSet, thresholds) {
  if (h[vertex] <= 0) return "water";
  if (riverSet.has(vertex)) return "river";
  if (nearRiverSet.has(vertex)) return "near river";

  const slope = slopeData.get(vertex) || 0;
  if (slope < thresholds.slopePlain) return "plain";
  if (slope < thresholds.slopeHill) return "hill";
  // Steep but low is a bluff or a cut bank, not a mountain. Without this the coast reads as a
  // mountain range, which is how the old classifier managed 45 mountain steps on a route that was
  // also 40% water.
  if (h[vertex] < thresholds.heightHigh) return "hill";
  return "mountain";
}

/**
 * Build the road, and with it the route and the terrain along it.
 *
 * Assigns `render.route`, `render.features` and `render.waypoints`, and returns the road as path
 * data for the SVG — a single polyline, since that is exactly what the route is.
 */
function buildRoad(render, slopeData) {
  const { h, cities: allCities, rivers, thresholds } = render;
  const mesh = h.mesh;

  // Sets, not the arrays these came from: the classifier and the cost function both ask about
  // river membership per vertex, and `indices.includes(v)` was a linear scan over every river
  // vertex in the world, once per edge relaxation.
  const riverSet = new Set(rivers.metadata.indices);
  const nearRiverSet = new Set(rivers.metadata.neighbours);

  const slopeRef = thresholds.slopeRef || 1;
  const edgeCost = (from, to) => {
    const d = distance(mesh, from, to);
    if (h[to] <= 0) return d * COST.WATER;
    let multiplier = 1 + COST.SLOPE * ((slopeData.get(to) || 0) / slopeRef);
    if (riverSet.has(to)) multiplier += COST.RIVER;
    return d * multiplier;
  };

  // The fallback is distance alone. It cannot help if the mesh is genuinely disconnected, but it is
  // the honest retry, and what matters is that route and features are never left empty or undefined
  // — GameContext reads features.length at mount, before anything renders.
  const leg = (from, to) =>
    findLeg(mesh, from, to, edgeCost) ||
    findLeg(mesh, from, to, (a, b) => distance(mesh, a, b)) || [from, to];

  const waypoints = selectWaypoints(mesh, mainlandCities(mesh, h, allCities));
  debug("waypoints", waypoints.length, "of", allCities.length);

  const route = [];
  for (let i = 1; i < waypoints.length; i++) {
    const section = leg(waypoints[i - 1], waypoints[i]);

    // Drop the joint: the section starts on the vertex the previous one ended on, and a repeated
    // vertex in the route would read as a step that went nowhere.
    for (let j = route.length === 0 ? 0 : 1; j < section.length; j++) {
      route.push(section[j]);
    }
  }

  render.route = route;
  // Parallel to route, not one short. Features used to be built per *edge*, so features[step]
  // described route[step + 1] and the player was told the terrain of the vertex ahead of the one
  // they were standing on.
  render.features = route.map((vertex) =>
    terrainAt(vertex, h, slopeData, riverSet, nearRiverSet, thresholds),
  );
  render.waypoints = waypoints;

  debug("route", route.length, "features", render.features.length);

  // One ordered polyline. drawPaths takes an array of paths, each an array of points.
  return [route.map((vertex) => mesh.vxs[vertex])];
}

export { buildRoad, selectWaypoints };
